import React from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  CheckCircle,
  ArrowRight,
  FileText,
  Book,
  Music,
  Image as ImageIcon,
  Video,
  Award,
  Users,
} from "lucide-react";
import TestimonialsSlider from "../components/TestimonialsSlider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const CopyrightRegistrationNew = () => {
  const navigate = useNavigate();

  const pricingPlans = [
    {
      name: "Gold",
      price: 99,
      subtitle: "+ USPTO Filing Fee",
      features: [
        "Professional Preparation of your federal copyright application",
        "Review by copyright team for accuracy and completeness",
        "Federal E-Filing with USPTO",
        "Certificate of Registration mailed to you",
      ],
    },
    {
      name: "Diamond",
      price: 149,
      subtitle: "+ USPTO Filing Fee",
      popular: true,
      features: [
        "Everything in Gold package",
        "Cease & Desist Letter template",
        "Transfer/Assignment template",
        "24 Hours Expedited Processing",
        "Priority review and filing",
      ],
    },
  ];

  const faqs = [
    {
      q: "What can be copyrighted?",
      a: "Original works including literary, artistic, musical compositions, software code, photographs, videos, and other creative expressions can be copyrighted.",
    },
    {
      q: "How long does copyright last?",
      a: "Copyright protection lasts for the creator's lifetime plus 70 years for individual works created after 1978.",
    },
    {
      q: "Do I need to register my copyright?",
      a: "Copyright exists automatically upon creation, but registration provides legal benefits including the ability to sue for infringement and statutory damages.",
    },
    {
      q: "What is the filing fee?",
      a: "USPTO filing fees vary by type of work but typically range from $45-$65, which is separate from our service fees.",
    },
    {
      q: "How long does registration take?",
      a: "Electronic registration typically takes 3-7 months, though processing times vary by the Copyright Office workload.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* About the Service */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-5xl font-bold text-gray-800 mb-6">
              Copyright Registration Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Protect your original creative works with professional copyright
              registration. From literary works to digital content, we safeguard
              your intellectual property rights.
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/")}
              className="bg-brand-gradient text-white hover:opacity-90 group shadow-lg font-bold text-lg px-8 py-6"
            >
              Get Started Today
              <ArrowRight
                className="ml-2 group-hover:translate-x-1 transition-transform"
                size={24}
              />
            </Button>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">
            Our Simple{" "}
            <span className="text-brand-primary">3-Step Process</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: FileText,
                title: "Submit Your Work",
                desc: "Provide details about your creative work and upload any necessary files.",
              },
              {
                icon: CheckCircle,
                title: "We Prepare & Review",
                desc: "Our team prepares your copyright application and reviews it for accuracy.",
              },
              {
                icon: Award,
                title: "File & Certify",
                desc: "We file with the Copyright Office and you receive your certificate.",
              },
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={idx}
                  className="text-center animate-scale-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="bg-brand-gradient w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why You Need It */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">
            Why You Need{" "}
            <span className="text-brand-primary">Copyright Protection</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Book,
                title: "Legal Proof",
                desc: "Establishes public record of your copyright ownership",
              },
              {
                icon: Music,
                title: "Exclusive Rights",
                desc: "Control reproduction and distribution of your work",
              },
              {
                icon: ImageIcon,
                title: "Prevent Theft",
                desc: "Take legal action against unauthorized use",
              },
              {
                icon: Video,
                title: "Statutory Damages",
                desc: "Eligible for statutory damages in infringement cases",
              },
              {
                icon: Award,
                title: "Business Asset",
                desc: "Your copyright becomes a valuable property asset",
              },
              {
                icon: Users,
                title: "Licensing Rights",
                desc: "License your work to others for profit",
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all animate-scale-in text-center"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <Icon className="text-brand-primary mx-auto mb-4" size={40} />
                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-base text-gray-600">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Affordable Copyright Packages
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Professional copyright registration at competitive prices
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingPlans.map((plan, idx) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-8 transition-all duration-300 hover:scale-105 flex flex-col h-full animate-scale-in bg-white shadow-lg hover:shadow-xl border border-emerald-100 ${plan.popular ? "ring-4 ring-emerald-200" : ""}`}
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-yellow-400 text-gray-800 px-4 py-1 rounded-full text-sm font-bold">
                      POPULAR
                    </div>
                  </div>
                )}
                <div className="text-center mb-6">
                  <p className="text-sm mb-2 font-semibold text-gray-500">
                    {plan.subtitle}
                  </p>
                  <h3 className="text-2xl font-bold mb-2 text-gray-800">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-bold text-brand-primary">
                      ${plan.price}
                    </span>
                  </div>
                </div>
                <div className="flex-grow mb-6 overflow-y-auto max-h-64 pr-2 scrollbar-thin">
                  <div className="space-y-4">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-start space-x-2">
                        <CheckCircle
                          className="flex-shrink-0 mt-0.5 text-brand-primary"
                          size={20}
                        />
                        <span className="text-sm font-medium text-gray-600">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={() => navigate("/")}
                  className="w-full mt-auto font-bold bg-brand-gradient text-white hover:opacity-90 shadow-lg"
                >
                  Choose Plan
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16 animate-fade-in">
            Why Choose{" "}
            <span className="text-brand-primary">Trademark Law Associates</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Award,
                title: "Copyright Experts",
                desc: "Specialized team with copyright law expertise",
              },
              {
                icon: CheckCircle,
                title: "Accurate Filing",
                desc: "Error-free applications reviewed by professionals",
              },
              {
                icon: Users,
                title: "Dedicated Support",
                desc: "Personal assistance throughout the process",
              },
              {
                icon: FileText,
                title: "Fast Processing",
                desc: "24-hour expedited service available",
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="text-center animate-scale-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="text-brand-primary" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-base text-gray-600">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="animate-fade-in">
              <img
                src="https://images.unsplash.com/photo-1574100004472-e536d3b6bacc?w=800"
                alt="Copyright Benefits"
                className="rounded-2xl shadow-xl hover-zoom"
              />
            </div>
            <div className="animate-slide-up">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                Key Benefits of Our Service
              </h2>
              <div className="space-y-4">
                {[
                  "Professional application preparation by copyright specialists",
                  "Comprehensive review for accuracy and completeness",
                  "Electronic filing for faster processing",
                  "Cease and desist letter templates included",
                  "24-hour expedited service option",
                  "Certificate of registration delivered to you",
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle
                      className="text-brand-primary flex-shrink-0 mt-1"
                      size={24}
                    />
                    <p className="text-lg text-gray-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <TestimonialsSlider /> */}

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">
              Frequently Asked{" "}
              <span className="text-brand-primary">Questions</span>
            </h2>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left font-semibold text-gray-800">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-gray-600">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h2 className="text-4xl font-bold text-white mb-6">
              Protect Your Creative Work Today
            </h2>
            <p className="text-xl text-blue-50 mb-8">
              Register your copyright and secure your intellectual property
              rights
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/")}
              className="bg-white text-brand-primary hover:bg-gray-50 shadow-xl border-2 border-brand-primary"
            >
              Get Started Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CopyrightRegistrationNew;
